// notificationSchema.js — shared definitions + validation for the custom
// notification flow rules (admin_config keys `notificationRules`,
// `caseSubmittedNotification`, `treatmentSubmitNotify`, `recipientGroups`,
// `scheduledNotificationsEnabled`).
//
// A rule (v2) = { id, name, enabled, kind, triggers[], refire, perCase?, coalesce?,
//                 conditions, recipients[], subjectSuffix?, note? }.
//   kind        — 'event' (fires on a case write) or 'scheduled' (daily scan);
//                 absent = 'event' (every stored v1 rule).
//   triggers    — event rules only: 1+ case events from NOTIFICATION_TRIGGERS.
//                 Legacy v1 rules carry a single `trigger` string instead; it
//                 still validates and normalizeRule() folds it into a 1-element
//                 `triggers` array on read.
//   refire      — scheduled rules only: 'once' (default) or 'rearm_on_activity'.
//   perCase     — scheduled rules only, OPTIONAL boolean (default false): the
//                 daily scan sends ONE email PER matching case (recipients
//                 resolved per case row, so a cc HoRO / Country manager lands on
//                 the SAME email as the clinic's To) using the caseReminderHtml
//                 template, with a ledger row per case (source
//                 'scheduled_per_case'). false = the digest-per-rule path.
//   coalesce    — event rules only, OPTIONAL:
//                 { windowMinutes:<int 2..60>, maxWindowMinutes?:<int> }.
//                 When set, rapid matching events are BUFFERED into a durable
//                 pending store and flushed as ONE "N updates" digest by
//                 flushPendingNotifications. windowMinutes is a DEBOUNCE quiet
//                 period — the group flushes once no new event has arrived for
//                 that many minutes (each new event pushes it back).
//                 maxWindowMinutes is an OPTIONAL safety cap — the group also
//                 flushes once it has been buffering that long since its FIRST
//                 event, so a continuously-active case can't stall forever
//                 (absent → COALESCE_MAX_WINDOW_DEFAULT applied at flush time).
//                 Absent/null coalesce = immediate (the back-compatible default).
//   conditions  — a rule tree in the SAME shape the auto-tag engine uses,
//                 restricted PER KIND (see conditionFieldError below).
//   recipients  — typed specs { kind, value } resolved server-side at send time
//   escalation  — scheduled rules only, OPTIONAL: [{ afterDays, recipients[] }]
//                 later steps of the same rule. The rule's day-count condition
//                 (see findDayThreshold) is step 1 with the rule's own
//                 recipients; each step re-runs it at its own threshold and a
//                 case is sent ONLY its highest reached step (expandEscalation).
//   subjectSuffix — optional per-rule subject tail (max 80 chars, trimmed)
//   note        — optional short note rendered into the email body (max 300)
//
// Security: recipients resolve to STAFF emails only (STAFF_EMAIL_DOMAINS), capped
// at MAX_RECIPIENTS_PER_DISPATCH. Notifications never carry client/clinical data.

import {
  validateRule,
  STRING_FIELDS,
  CONTEXT_STRING_FIELDS,
  NUMERIC_FIELDS,
  PREVIOUS_FIELDS,
  REQUIREMENT_FIELDS,
  TEMPORAL_FIELDS,
} from './ruleEngine.js';

/** Case events that can fire an EVENT notification rule. */
export const NOTIFICATION_TRIGGERS = Object.freeze([
  'case_submitted',
  'case_updated',              // a PATCH produced ≥1 generic field-edit / type_specific event
  'status_changed',
  'severity_changed',
  'attachment_uploaded',       // a case attachment upload (postCaseAttachment)
  'requirement_signed_off',
  'requirement_added',         // a requirement (document/sign-off request) added (postCaseRequirement)
  'comment_added',             // a PUBLIC comment posted (postComment)
  'comment_added_private',     // a PRIVATE comment posted — deliberate opt-in; the rule author must
                               // pick private-cleared recipients (email is existence-only, never content)
]);

/** Rule kinds — how a rule is evaluated. */
export const NOTIFICATION_RULE_KINDS = Object.freeze(['event', 'scheduled']);

/**
 * Scheduled-rule refire modes ('once' = default).
 *   once              — a case ever notified for the rule is done.
 *   rearm_on_activity — fires again once activity is NEWER than the last send.
 *   every_run         — daily-report mode: sends every scan, NO dedup read and
 *                       NO per-case ledger rows (a daily report must not spam
 *                       every case timeline).
 */
export const NOTIFICATION_REFIRE_MODES = Object.freeze(['once', 'rearm_on_activity', 'every_run']);

/** The triggers that populate the previous* condition fields at dispatch. */
export const CHANGE_TRIGGERS = Object.freeze(['status_changed', 'severity_changed']);

/** The triggers that populate the requirement* condition fields at dispatch. */
export const REQUIREMENT_TRIGGERS = Object.freeze(['requirement_added', 'requirement_signed_off']);

/** Per-rule subject suffix / body note length caps. */
export const SUBJECT_SUFFIX_MAX = 80;
export const NOTE_MAX = 300;

/** Coalesce (batch rapid updates) window bounds, in whole minutes.
 *  windowMinutes is the DEBOUNCE quiet-period (flush once quiet this long).
 *  maxWindowMinutes is the OPTIONAL safety cap (flush this long after the FIRST
 *  event regardless of activity); absent → COALESCE_MAX_WINDOW_DEFAULT at flush. */
export const COALESCE_WINDOW_MIN = 2;
export const COALESCE_WINDOW_MAX = 60;
export const COALESCE_MAX_WINDOW_DEFAULT = 30;
export const COALESCE_MAX_WINDOW_MAX = 240;

/** Delivery channel for a recipient. */
export const RECIPIENT_CHANNELS = Object.freeze(['to', 'cc', 'bcc']);

/**
 * The 'except' channel: a spec on this channel is resolved like any other and
 * its addresses are REMOVED from the result (e.g. a role except its head).
 * Kept out of RECIPIENT_CHANNELS, which lists the delivery channels.
 */
export const EXCEPT_CHANNEL = 'except';
const isExcept = (spec) => spec?.channel === EXCEPT_CHANNEL;
/** Specs that actually deliver (not the 'except' ones). */
export const deliveringSpecs = (specs) => (Array.isArray(specs) ? specs : []).filter(s => !isExcept(s));
/** The 'except' specs. */
export const exceptSpecs = (specs) => (Array.isArray(specs) ? specs : []).filter(isExcept);

/** How a recipient is resolved at send time. */
export const RECIPIENT_KINDS = Object.freeze([
  'email',                     // a literal staff email
  'horo_by_region',            // all HoROs whose region matches the case
  'clinic_manager_by_clinic',  // clinic management team — all CMs + AMs for the case clinic
  'jobTitle',                  // all directory users with this exact job title
  'role',                      // a named preset → a job title (see ROLE_PRESETS)
  'group',                     // a named admin-managed list (config key recipientGroups)
  'country_manager_by_ops',    // holders of any role profile scoped countryGroup = the case's ops (IOI/GB/US)
  'appRole',                   // every holder (direct or via group) of this AE Tracker Entra app role value
]);

/**
 * Named role presets → the directory job title they resolve to. Lets admins pick
 * "Injectables lead" without typing the exact Entra job title. Extend here only.
 */
export const ROLE_PRESETS = Object.freeze({
  injectables_lead: { label: 'Injectables lead', jobTitle: 'Head of Injectable Growth UK & ROI' },
  hs_compliance_manager: { label: 'H&S Compliance manager', jobTitle: 'Health & Safety Compliance Manager' },
});

/** Recipients must resolve to staff addresses on these domains (never clients).
 *  `.us` / `.ph` are distinct TLDs, so each must be listed explicitly (the
 *  `.endsWith('.'+d)` rule below only covers subdomains). First entry is the
 *  default in the StaffEmailInput domain dropdown — keep .com first. */
export const STAFF_EMAIL_DOMAINS = Object.freeze(['therapieclinic.com', 'therapieclinic.us', 'therapieclinic.ph']);

/** Hard cap on resolved emails per single dispatch — a backstop against storms. */
export const MAX_RECIPIENTS_PER_DISPATCH = 50;

const TRIGGER_SET = new Set(NOTIFICATION_TRIGGERS);
const KIND_SET    = new Set(NOTIFICATION_RULE_KINDS);
const REFIRE_SET  = new Set(NOTIFICATION_REFIRE_MODES);
const RECIPIENT_KIND_SET = new Set(RECIPIENT_KINDS);
const PREVIOUS_FIELD_SET = new Set(PREVIOUS_FIELDS);
const REQUIREMENT_FIELD_SET = new Set(REQUIREMENT_FIELDS);
const TEMPORAL_FIELD_SET = new Set(TEMPORAL_FIELDS);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RULES = 100;

// ── Per-kind condition field allowlists (the notification-side gate) ──────────
// The engine (ruleEngine.js) owns structural validation of the whole superset;
// here we restrict WHICH fields each rule kind may reference:
//   base (both kinds)  — the 9 case string fields + ops + severityLevel.
//   EVENT rules        — base + fileCount + outstandingRequirement nodes;
//                        previous* only when a change trigger
//                        (status_changed / severity_changed) is present;
//                        requirement* only when a requirement trigger
//                        (requirement_added / requirement_signed_off) is present.
//   SCHEDULED rules    — base + fileCount + outstandingRequirement + ALL
//                        temporal day-counts; never previous* or requirement*.
// Aggregate (cross-case count) nodes stay banned for every notification rule.
const BASE_CONDITION_FIELDS = [
  ...STRING_FIELDS,
  ...CONTEXT_STRING_FIELDS.filter(f => !PREVIOUS_FIELD_SET.has(f) && !REQUIREMENT_FIELD_SET.has(f)),   // → ops
  ...NUMERIC_FIELDS.filter(f => !PREVIOUS_FIELD_SET.has(f)),          // → severityLevel
];
const SCHEDULED_FIELD_SET = new Set([...BASE_CONDITION_FIELDS, ...TEMPORAL_FIELDS]);

/** The fields an EVENT rule with this trigger set may condition on. */
function eventFieldSetForTriggers(triggers) {
  const fields = [...BASE_CONDITION_FIELDS];
  if (triggers.some(t => CHANGE_TRIGGERS.includes(t))) fields.push(...PREVIOUS_FIELDS);
  if (triggers.some(t => REQUIREMENT_TRIGGERS.includes(t))) fields.push(...REQUIREMENT_FIELDS);
  return new Set(fields);
}

export function isStaffEmail(addr) {
  const s = String(addr || '').trim().toLowerCase();
  if (!EMAIL_RE.test(s)) return false;
  const domain = s.split('@')[1];
  return STAFF_EMAIL_DOMAINS.some(d => domain === d || domain.endsWith(`.${d}`));
}

/**
 * normalizeStaffMail(raw) — coerce a directory `mail` value into a clean, sendable
 * address, or null when nothing address-like is present. Directory exports can
 * carry "Display Name <addr@x.com>", a `mailto:` prefix, stray whitespace, or an
 * empty string; storing those verbatim silently breaks every downstream send
 * (isStaffEmail rejects them). Callers skip the record when this returns null
 * rather than persist an unsendable value. Domain is NOT checked here — that is
 * isStaffEmail's job at send time; this only guarantees a bare, well-formed address.
 */
export function normalizeStaffMail(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;
  const angle = s.match(/<([^>]+)>/);          // "Name <addr>" → addr
  if (angle) s = angle[1];
  s = s.replace(/^mailto:/i, '').replace(/\s+/g, '').trim();
  return EMAIL_RE.test(s) ? s : null;
}

// ── Rule normalization ─────────────────────────────────────────────────────────

/** The effective trigger list for a rule: `triggers` array, else legacy `trigger`. */
function normalizedTriggers(rule) {
  if (Array.isArray(rule?.triggers) && rule.triggers.length) return [...new Set(rule.triggers)];
  if (rule?.trigger != null) return [rule.trigger];
  return [];
}

/**
 * normalizeRule(rule) — non-mutating canonical v2 shape for readers (dispatch,
 * the daily scan, preview):
 *   kind          — 'event' | 'scheduled' (defaulted to 'event').
 *   triggers      — string[] for event rules (legacy `trigger` folded in, deduped);
 *                   ALWAYS [] for scheduled rules so they never match a dispatch.
 *   refire        — scheduled rules only, defaulted to 'once'; absent on event rules.
 *   perCase       — scheduled rules only, a strict boolean (true only when stored
 *                   as exactly true); absent on event rules.
 *   coalesce      — event rules only: passed through UNCHANGED when present;
 *                   dropped on scheduled rules (never valid there).
 *   subjectSuffix / note — trimmed; absent when blank.
 * The legacy `trigger` key is dropped. All other properties pass through untouched.
 */
export function normalizeRule(rule) {
  if (!rule || typeof rule !== 'object') return rule;
  const kind = rule.kind === 'scheduled' ? 'scheduled' : 'event';
  const out = { ...rule, kind };
  delete out.trigger;
  if (kind === 'event') {
    out.triggers = normalizedTriggers(rule);
    delete out.refire;
    delete out.perCase;    // per-case sending is a scheduled-scan concept
    delete out.escalation; // escalation steps are a scheduled-scan concept
    // coalesce passes through untouched (validated by validateNotificationRule).
  } else {
    out.triggers = [];
    out.refire = REFIRE_SET.has(rule.refire) ? rule.refire : 'once';
    out.perCase = rule.perCase === true;
    delete out.coalesce;   // scheduled rules never coalesce
  }
  const suffix = typeof rule.subjectSuffix === 'string' ? rule.subjectSuffix.trim() : '';
  if (suffix) out.subjectSuffix = suffix; else delete out.subjectSuffix;
  const note = typeof rule.note === 'string' ? rule.note.trim() : '';
  if (note) out.note = note; else delete out.note;
  return out;
}

// ── Per-kind condition gate ────────────────────────────────────────────────────

function disallowedFieldMessage(field, kind) {
  if (PREVIOUS_FIELD_SET.has(field)) {
    return kind === 'scheduled'
      ? `condition field "${field}" is not available in scheduled rules`
      : `condition field "${field}" needs a status_changed or severity_changed trigger`;
  }
  if (REQUIREMENT_FIELD_SET.has(field)) {
    return kind === 'scheduled'
      ? `condition field "${field}" is not available in scheduled rules`
      : `condition field "${field}" needs a requirement_added or requirement_signed_off trigger`;
  }
  if (TEMPORAL_FIELD_SET.has(field)) {
    return `condition field "${field}" is only available in scheduled rules`;
  }
  return `unsupported condition field "${field}"`;
}

// Walk the tree; returns the first kind-gate error string or null. Structural
// validity (shapes, ops, bounds, unknown fields) is validateRule's job.
function conditionFieldError(node, allowedFields, kind, depth = 0) {
  if (node == null) return null;
  if (depth > 12) return 'conditions nested too deep';
  if (node.type === 'aggregate') {
    return 'conditions may not use cross-case count rules';
  }
  if (node.type === 'fileCount') return null;               // allowed for both kinds
  if (node.type === 'outstandingRequirement') return null;  // allowed for both kinds (ctx.outstanding)
  if (Array.isArray(node.rules)) {
    for (const child of node.rules) {
      const e = conditionFieldError(child, allowedFields, kind, depth + 1);
      if (e) return e;
    }
    return null;
  }
  if (node.field != null && !allowedFields.has(node.field)) {
    return disallowedFieldMessage(node.field, kind);
  }
  return null;
}

// ── Recipient specs ────────────────────────────────────────────────────────────

const CHANNEL_SET = new Set(RECIPIENT_CHANNELS);

function validateRecipientSpec(spec) {
  if (!spec || typeof spec !== 'object') return 'recipient must be an object';
  if (!RECIPIENT_KIND_SET.has(spec.kind)) return `recipient kind must be one of ${RECIPIENT_KINDS.join(', ')}`;
  if (spec.channel != null && !CHANNEL_SET.has(spec.channel) && spec.channel !== EXCEPT_CHANNEL) {
    return `channel must be one of ${[...RECIPIENT_CHANNELS, EXCEPT_CHANNEL].join(', ')}`;
  }
  if (spec.kind === 'email') {
    if (!isStaffEmail(spec.value)) return `recipient email must be a staff address (${STAFF_EMAIL_DOMAINS.join(', ')})`;
  } else if (spec.kind === 'jobTitle') {
    if (!spec.value || !String(spec.value).trim()) return 'jobTitle recipient needs a value';
  } else if (spec.kind === 'role') {
    if (!ROLE_PRESETS[spec.value]) return `unknown role preset "${spec.value}"`;
  } else if (spec.kind === 'appRole') {
    if (!spec.value || !String(spec.value).trim()) return 'appRole recipient needs a value (the app role)';
    if (String(spec.value).length > 100) return 'appRole value is too long';
  } else if (spec.kind === 'group') {
    // The key is checked against the recipientGroups config at SEND time (an
    // unknown key soft-fails to zero addresses); here we only require a key.
    if (!spec.value || !String(spec.value).trim()) return 'group recipient needs a value (the group key)';
  }
  // horo_by_region / clinic_manager_by_clinic / country_manager_by_ops take no value
  return null;
}

// ── Rule / rule-list validation ────────────────────────────────────────────────

/** Validate one custom rule's structure (v1 or v2 shape). Returns string[] of errors. */
export function validateNotificationRule(rule) {
  const errors = [];
  if (!rule || typeof rule !== 'object') return ['rule must be an object'];
  if (!rule.id || !String(rule.id).trim()) errors.push('rule needs an id');
  if (!rule.name || !String(rule.name).trim()) errors.push('rule needs a name');

  if (rule.kind != null && !KIND_SET.has(rule.kind)) {
    errors.push(`kind must be one of ${NOTIFICATION_RULE_KINDS.join(', ')}`);
  }
  const kind = rule.kind === 'scheduled' ? 'scheduled' : 'event';
  const triggers = normalizedTriggers(rule);

  if (kind === 'event') {
    if (rule.refire != null) errors.push('refire only applies to scheduled rules');
    if (rule.perCase != null) errors.push('perCase (one email per case) only applies to scheduled rules');
    if (rule.triggers != null && !Array.isArray(rule.triggers)) {
      errors.push('triggers must be an array');
    }
    if (triggers.length === 0) {
      errors.push('event rule needs at least one trigger');
    } else if (triggers.some(t => !TRIGGER_SET.has(t))) {
      errors.push(`trigger must be one of ${NOTIFICATION_TRIGGERS.join(', ')}`);
    }
    // coalesce (opt-in batching) — event rules only, optional; when present it
    // must be { windowMinutes:<int 2..60>, maxWindowMinutes?:<int> }.
    // windowMinutes = debounce quiet-period; maxWindowMinutes = optional cap.
    if (rule.coalesce != null) {
      if (typeof rule.coalesce !== 'object' || Array.isArray(rule.coalesce)) {
        errors.push('coalesce must be an object { windowMinutes }');
      } else {
        const w = rule.coalesce.windowMinutes;
        if (!Number.isInteger(w) || w < COALESCE_WINDOW_MIN || w > COALESCE_WINDOW_MAX) {
          errors.push(`coalesce.windowMinutes must be a whole number between ${COALESCE_WINDOW_MIN} and ${COALESCE_WINDOW_MAX}`);
        }
        const m = rule.coalesce.maxWindowMinutes;
        if (m != null) {
          if (!Number.isInteger(m) || m < w || m > COALESCE_MAX_WINDOW_MAX) {
            errors.push(`coalesce.maxWindowMinutes must be a whole number between windowMinutes (${Number.isInteger(w) ? w : COALESCE_WINDOW_MIN}) and ${COALESCE_MAX_WINDOW_MAX}`);
          }
        }
      }
    }
  } else {
    if (triggers.length > 0) errors.push('scheduled rules do not take triggers');
    if (rule.refire != null && !REFIRE_SET.has(rule.refire)) {
      errors.push(`refire must be one of ${NOTIFICATION_REFIRE_MODES.join(', ')}`);
    }
    if (rule.perCase != null && typeof rule.perCase !== 'boolean') {
      errors.push('perCase must be true or false');
    }
    if (rule.coalesce != null) {
      errors.push('coalesce (batch rapid updates) is only valid on event rules');
    }
  }
  if (rule.escalation != null) {
    if (kind !== 'scheduled') errors.push('escalation steps only apply to scheduled rules');
    else errors.push(...escalationErrors(rule));
  }

  if (rule.subjectSuffix != null) {
    if (typeof rule.subjectSuffix !== 'string') errors.push('subjectSuffix must be a string');
    else if (rule.subjectSuffix.trim().length > SUBJECT_SUFFIX_MAX) errors.push(`subjectSuffix is too long (max ${SUBJECT_SUFFIX_MAX})`);
  }
  if (rule.note != null) {
    if (typeof rule.note !== 'string') errors.push('note must be a string');
    else if (rule.note.trim().length > NOTE_MAX) errors.push(`note is too long (max ${NOTE_MAX})`);
  }

  // Conditions optional (no conditions = always fire for the trigger / scan).
  if (rule.conditions != null) {
    const rv = validateRule(rule.conditions);
    if (!rv.ok) errors.push(...rv.errors);
    const allowed = kind === 'scheduled'
      ? SCHEDULED_FIELD_SET
      : eventFieldSetForTriggers(triggers);
    const fe = conditionFieldError(rule.conditions, allowed, kind);
    if (fe) errors.push(fe);
  }

  if (!Array.isArray(rule.recipients) || rule.recipients.length === 0) {
    errors.push('rule needs at least one recipient');
  } else {
    rule.recipients.forEach((r, i) => {
      const e = validateRecipientSpec(r);
      if (e) errors.push(`recipient ${i + 1}: ${e}`);
    });
    if (!deliveringSpecs(rule.recipients).length) errors.push('rule needs at least one To, Cc or Bcc recipient (not only Except)');
  }
  return errors;
}

// ── Escalation steps (scheduled rules) ─────────────────────────────────────────

/** Max escalation steps after the rule's own (step 1). */
export const MAX_ESCALATION_STEPS = 5;
const MAX_ESCALATION_DAYS = 3650;
/** Day-count fields that only grow, so a higher threshold means "later". */
export const ESCALATION_DAY_FIELDS = Object.freeze([
  'daysFromIncident', 'daysFromReported', 'daysInCurrentStatus', 'daysSinceLastActivity',
]);
const ESCALATION_DAY_FIELD_SET = new Set(ESCALATION_DAY_FIELDS);

// The first day-count node in document order: an outstandingRequirement node
// (its minDays) or a growing temporal leaf compared with >= / >.
function findDayNode(node, path = [], depth = 0) {
  if (!node || typeof node !== 'object' || depth > 12) return null;
  if (node.type === 'outstandingRequirement') return { path, days: Number.isInteger(node.minDays) ? node.minDays : 0 };
  if (Array.isArray(node.rules)) {
    for (let i = 0; i < node.rules.length; i++) {
      const hit = findDayNode(node.rules[i], [...path, i], depth + 1);
      if (hit) return hit;
    }
    return null;
  }
  if (ESCALATION_DAY_FIELD_SET.has(node.field) && typeof node.value === 'number') {
    if (node.op === 'greaterOrEqual') return { path, days: node.value };
    if (node.op === 'greaterThan') return { path, days: node.value + 1 };
  }
  return null;
}

/**
 * findDayThreshold(conditions) → the rule's day threshold (whole days) or null.
 * This is what escalation steps re-run at their own `afterDays`.
 */
export function findDayThreshold(conditions) {
  const hit = findDayNode(conditions);
  return hit ? hit.days : null;
}

/** A copy of `conditions` with its day-count node set to ">= days". */
export function withDayThreshold(conditions, days) {
  const hit = findDayNode(conditions);
  if (!hit) return conditions;
  const copy = JSON.parse(JSON.stringify(conditions));
  let node = copy;
  for (const i of hit.path) node = node.rules[i];
  if (node.type === 'outstandingRequirement') node.minDays = days;
  else { node.op = 'greaterOrEqual'; node.value = days; }
  return copy;
}

function escalationErrors(rule) {
  const errors = [];
  if (!Array.isArray(rule.escalation)) return ['escalation must be a list of steps'];
  if (rule.escalation.length > MAX_ESCALATION_STEPS) errors.push(`at most ${MAX_ESCALATION_STEPS} escalation steps`);
  const base = findDayThreshold(rule.conditions);
  if (base == null && rule.escalation.length) {
    errors.push('escalation needs a day-count condition to step up from (e.g. "outstanding for at least N days" or "days since last activity ≥ N")');
  }
  let prev = base ?? 0;
  rule.escalation.forEach((step, i) => {
    const label = `escalation step ${i + 2}`;
    if (!step || typeof step !== 'object') { errors.push(`${label}: must be an object`); return; }
    const d = step.afterDays;
    if (!Number.isInteger(d) || d < 1 || d > MAX_ESCALATION_DAYS) errors.push(`${label}: days must be a whole number between 1 and ${MAX_ESCALATION_DAYS}`);
    else if (d <= prev) errors.push(`${label}: must be later than ${prev} day(s) (the step before it)`);
    if (Number.isInteger(d)) prev = Math.max(prev, d);
    if (!Array.isArray(step.recipients) || !step.recipients.length) errors.push(`${label}: needs at least one recipient`);
    else {
      step.recipients.forEach((r, j) => {
        const e = validateRecipientSpec(r);
        if (e) errors.push(`${label}, recipient ${j + 1}: ${e}`);
      });
      if (!deliveringSpecs(step.recipients).length) errors.push(`${label}: needs at least one To, Cc or Bcc recipient`);
    }
  });
  return errors;
}

/**
 * expandEscalation(rule) — a normalised scheduled rule → the rules the daily
 * scan actually runs. No escalation → [rule] unchanged. Otherwise one rule per
 * step: step 1 keeps the rule's id/recipients at its own threshold; step k
 * gets id `<id>~esc<k>` (its own dedup ledger key), the step's recipients and
 * the conditions re-thresholded to its afterDays. Every step but the last
 * carries `notConditions` (the NEXT step's conditions) so a case only ever
 * gets its highest reached step.
 */
export function expandEscalation(rule) {
  const steps = Array.isArray(rule?.escalation) ? rule.escalation : [];
  const base = findDayThreshold(rule?.conditions);
  if (!steps.length || base == null) return [rule];
  const thresholds = [base, ...steps.map(s => s.afterDays)];
  return thresholds.map((days, k) => {
    const out = {
      ...rule,
      conditions: withDayThreshold(rule.conditions, days),
      escalationStep: k + 1,
    };
    delete out.escalation;
    if (k > 0) {
      out.id = `${rule.id}~esc${k + 1}`;
      out.name = `${rule.name} · escalation (${days}+ days)`;
      out.recipients = steps[k - 1].recipients;
    }
    if (k < thresholds.length - 1) out.notConditions = withDayThreshold(rule.conditions, thresholds[k + 1]);
    return out;
  });
}

/** Validate the whole `notificationRules` array. Returns { ok, errors, byId }. */
export function validateNotificationRules(value) {
  const errors = [];
  const byId = {};
  if (!Array.isArray(value)) return { ok: false, errors: ['notificationRules must be an array'], byId };
  if (value.length > MAX_RULES) errors.push(`too many rules (max ${MAX_RULES})`);
  const seenIds = new Set();
  for (const rule of value) {
    const rErrors = validateNotificationRule(rule);
    const id = rule?.id || '(no id)';
    if (seenIds.has(id)) rErrors.push('duplicate rule id');
    seenIds.add(id);
    if (rErrors.length) { byId[id] = rErrors; errors.push(...rErrors.map(e => `${rule?.name || id}: ${e}`)); }
  }
  return { ok: errors.length === 0, errors, byId };
}

/**
 * Validate `treatmentSubmitNotify` — per-treatment extra recipients on the
 * new-case notification: [{ treatment, recipients[] }].
 */
export function validateTreatmentSubmitNotify(value) {
  const errors = [];
  if (!Array.isArray(value)) return { ok: false, errors: ['treatmentSubmitNotify must be an array'], byId: {} };
  const seen = new Set();
  value.forEach((row, i) => {
    if (!row || typeof row !== 'object') { errors.push(`Row ${i + 1} is not an object`); return; }
    const tx = String(row.treatment || '').trim();
    if (!tx) errors.push(`Row ${i + 1}: treatment is required`);
    else if (seen.has(tx.toUpperCase())) errors.push(`Duplicate treatment "${tx}"`);
    seen.add(tx.toUpperCase());
    if (!Array.isArray(row.recipients)) errors.push(`${tx || `Row ${i + 1}`}: recipients must be an array`);
    else row.recipients.forEach((r, j) => { const e = validateRecipientSpec(r); if (e) errors.push(`${tx || `Row ${i + 1}`} recipient ${j + 1}: ${e}`); });
  });
  return { ok: errors.length === 0, errors, byId: {} };
}

/** Validate the `caseSubmittedNotification` object { enabled, recipients[] }. */
export function validateCaseSubmittedNotification(value) {
  const errors = [];
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ok: false, errors: ['caseSubmittedNotification must be an object'], byId: {} };
  }
  if (typeof value.enabled !== 'boolean') errors.push('enabled must be a boolean');
  if (!Array.isArray(value.recipients)) errors.push('recipients must be an array');
  else value.recipients.forEach((r, i) => { const e = validateRecipientSpec(r); if (e) errors.push(`recipient ${i + 1}: ${e}`); });
  return { ok: errors.length === 0, errors, byId: {} };
}

// ── recipientGroups (config key) ───────────────────────────────────────────────

const GROUP_KEY_RE = /^[a-z0-9][a-z0-9_-]*$/;
const MAX_GROUP_KEY_LEN = 60;
/** Max addresses in one named group — the same storm backstop as a dispatch. */
export const MAX_GROUP_EMAILS = MAX_RECIPIENTS_PER_DISPATCH;

/**
 * Validate the `recipientGroups` config value:
 *   [ { key: <slug, unique>, label, emails: string[] } ]
 * Every address must be a staff address; max MAX_GROUP_EMAILS per group. An
 * empty emails list is allowed (a group may be staged before it is populated).
 * Returns { ok, errors, byId } keyed by group key.
 */
export function validateRecipientGroups(value) {
  const errors = [];
  const byId = {};
  if (!Array.isArray(value)) return { ok: false, errors: ['recipientGroups must be an array'], byId };
  const seen = new Set();
  value.forEach((group, i) => {
    if (!group || typeof group !== 'object' || Array.isArray(group)) {
      errors.push(`group ${i + 1}: must be an object`);
      return;
    }
    const key = String(group.key || '').trim();
    const push = (msg) => {
      if (key) (byId[key] ||= []).push(msg);
      errors.push(`${key || `group ${i + 1}`}: ${msg}`);
    };
    if (!key) push('needs a key');
    else if (key.length > MAX_GROUP_KEY_LEN) push(`key is too long (max ${MAX_GROUP_KEY_LEN})`);
    else if (!GROUP_KEY_RE.test(key)) push('key must be a slug (lowercase letters, digits, - or _)');
    if (key && seen.has(key.toLowerCase())) push(`duplicate group key "${key}"`);
    if (key) seen.add(key.toLowerCase());
    if (!group.label || !String(group.label).trim()) push('needs a label');
    if (!Array.isArray(group.emails)) {
      push('emails must be an array');
    } else {
      if (group.emails.length > MAX_GROUP_EMAILS) push(`too many addresses (max ${MAX_GROUP_EMAILS})`);
      group.emails.forEach((addr, j) => {
        if (!isStaffEmail(addr)) push(`address ${j + 1} must be a staff address (${STAFF_EMAIL_DOMAINS.join(', ')})`);
      });
    }
  });
  return { ok: errors.length === 0, errors, byId };
}

/**
 * Validate the `scheduledNotificationsEnabled` config value — the master switch
 * for the daily scheduled-rules scan. Must be a boolean; readers treat an
 * absent key as false (off).
 */
export function validateScheduledNotificationsEnabled(value) {
  const ok = typeof value === 'boolean';
  return { ok, errors: ok ? [] : ['scheduledNotificationsEnabled must be true or false'], byId: {} };
}
